import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuthContext } from './context/AuthContext'; // 1. Importar o AuthContext
import axios from 'axios'; // 2. Importar axios

const EntidadeContext = createContext(null);

export const EntidadeProvider = ({ children }) => {
  const { authState, loggedInUser } = useAuthContext();
  const [currentEntidade, _setCurrentEntidade] = useState(null); // Inicializa como null
  const [isLoadingEntidade, setIsLoadingEntidade] = useState(true);

  // --- DADOS DE CONFIGURAÇÃO DA AGENDA ---
  const [agendaConfig, setAgendaConfig] = useState({ feriados: [], diasBloqueados: [] });
  const [isLoadingAgendaConfig, setIsLoadingAgendaConfig] = useState(true);

  // --- NOVOS ESTADOS PARA DADOS DE SUPORTE DA ENTIDADE ---
  const [supportData, setSupportData] = useState({
    especialidades: [],
    todosMedicos: [],
    allFuncionarios: [],
    pacientesList: [],
    servicos: [],
  });
  const [isLoadingSupportData, setIsLoadingSupportData] = useState(true);

  const setCurrentEntidade = useCallback((entidade) => {
    _setCurrentEntidade(entidade);
    // Otimização: Se a entidade atualizada já contém os dados da agenda,
    // atualizamos o estado de agendaConfig diretamente, evitando um re-fetch.
    if (entidade && (entidade.feriados || entidade.diasBloqueados)) {
      setAgendaConfig({
        feriados: entidade.feriados || [],
        diasBloqueados: entidade.diasBloqueados || []
      });
    }
  }, []);

  // Função para adicionar um novo paciente à lista de suporte de dados
  const addPacienteToList = useCallback((newPaciente) => {
    setSupportData(prevSupportData => ({
      ...prevSupportData,
      pacientesList: [...prevSupportData.pacientesList, newPaciente].sort((a, b) => a.nome.localeCompare(b.nome))
    }));
  }, []);

  // Efeito para inicializar a entidade a partir do estado de autenticação
  useEffect(() => {
    if (loggedInUser && authState.entidade) {
      _setCurrentEntidade(authState.entidade);
      setIsLoadingEntidade(false);
    } else if (!loggedInUser) {
      // Limpa o estado quando o usuário faz logout
      _setCurrentEntidade(null);
      setIsLoadingEntidade(true);
    }
  }, [loggedInUser, authState.entidade]);

  // Efeito que busca a configuração da agenda QUANDO a entidade é definida
  useEffect(() => {
    const fetchAgendaConfig = async () => {
      // Condições para buscar:
      // 1. Usuário autenticado.
      // 2. Entidade definida.
      if (authState.isAuthenticated && currentEntidade?.id) {
        setIsLoadingAgendaConfig(true);
        try {
          // Este endpoint busca detalhes como feriados e dias bloqueados,
          // complementando os dados de horários que já vêm com a entidade no login.
          const response = await axios.get(`/api/entidades/${currentEntidade.id}/agenda-config`);
          setAgendaConfig(response.data || { feriados: [], diasBloqueados: [] });
        } catch (error) {
          console.error("Erro ao buscar configuração da agenda no EntidadeContext:", error);
          setAgendaConfig({ feriados: [], diasBloqueados: [] }); // Define um valor padrão em caso de erro
        } finally {
          setIsLoadingAgendaConfig(false);
        }
      } else if (!authState.isAuthenticated) {
        // Se o usuário deslogar, limpa a configuração
        setAgendaConfig({ feriados: [], diasBloqueados: [] });
        setIsLoadingAgendaConfig(true);
      }
    };

    fetchAgendaConfig();
  }, [authState.isAuthenticated, currentEntidade?.id]); // Depende do ID da entidade para refetch

  // Each list has its own permissions; a denial must not discard other results.
  useEffect(() => {
    let active = true;
    const empty = {
      especialidades: [], todosMedicos: [], allFuncionarios: [], pacientesList: [], servicos: [],
    };
    setSupportData(empty);
    if (!authState.isAuthenticated || !currentEntidade?.id) {
      setIsLoadingSupportData(false);
      return () => { active = false; };
    }
    setIsLoadingSupportData(true);
    const entityId = currentEntidade.id;
    const lists = [
      ['especialidades', 'especialidades', `/api/entidades/${entityId}/especialidades-corpo-clinico`],
      ['todosMedicos', 'funcionarios', `/api/entidades/${entityId}/funcionarios-corpo-clinico`],
      ['servicos', 'servicos', `/api/servicos?entidade_id=${entityId}`],
      ['pacientesList', 'pacientes', `/api/entidades/${entityId}/pacientes/basic-info`],
      ['allFuncionarios', 'funcionarios', `/api/funcionarios?entidade_id=${entityId}`],
    ];
    const fetchAllSupportData = async () => {
      const results = await Promise.allSettled(lists.map(([, , url]) => axios.get(url)));
      if (!active) return;
      const next = { ...empty };
      results.forEach((result, index) => {
        const [key, field, url] = lists[index];
        if (result.status === 'fulfilled') {
          const rows = result.value.data?.[field];
          next[key] = Array.isArray(rows) ? rows : [];
        } else {
          console.warn('Não foi possível carregar dados de suporte:', url,
            result.reason?.response?.status || 'erro de conexão');
        }
      });
      setSupportData(next);
      setIsLoadingSupportData(false);
    };
    fetchAllSupportData();
    // Ignore responses from a previous company or a session that has ended.
    return () => { active = false; };
  }, [authState.isAuthenticated, currentEntidade?.id]);

  const value = useMemo(() => ({
    currentEntidade,
    setCurrentEntidade,
    isLoadingEntidade,
    agendaConfig,
    isLoadingAgendaConfig,
    supportData, // Contém a pacientesList
    isLoadingSupportData,
    addPacienteToList // Expõe a nova função
  }), [
    currentEntidade, setCurrentEntidade, isLoadingEntidade,
    agendaConfig, isLoadingAgendaConfig,
    supportData, isLoadingSupportData,
    addPacienteToList // Adiciona ao array de dependências
  ]);

  return (
    <EntidadeContext.Provider value={value}>
      {children}
    </EntidadeContext.Provider>
  );
};

export const useEntidadeContext = () => {
  const context = useContext(EntidadeContext);
  if (context === null) {
    throw new Error('useEntidadeContext deve ser usado dentro de um EntidadeProvider');
  }
  return context;
};